import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-white hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "bg-white hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        pink: "bg-pink-200 hover:bg-pink-300 text-gray-800",
        orange: "bg-orange-500 text-white hover:bg-orange-600",
        "dove-button": "bg-[#A9C6CC] hover:bg-[#9DB8BE] text-white border border-white rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-200",
        /** Matches tool cards in AppPreviewBanner (e.g. Bordsplacering) */
        "cream-tool":
          "bg-[#EBE5DC] text-dark-blue shadow-md rounded-full font-bold border-transparent outline-none hover:opacity-90 hover:bg-[#EBE5DC] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-dark-blue transition-opacity duration-200",
        "white-with-blue-text-button": "bg-white hover:bg-gray-100 text-[#252e34] rounded-full font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300",
        "dark-blue": "bg-dark-blue text-white hover:bg-dark-blue/90",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
